/**
 * Notas de voz offline del chat IA (KAN-225) — helpers puros.
 * Spec: docs/superpowers/specs/2026-09-15-notas-de-voz-offline-chat-design.md
 */

/** Tope por nota: 3 minutos. */
export const MAX_VOICE_NOTE_MS = 180000;
/** Menos de 1 s = toque accidental: se descarta. */
export const MIN_VOICE_NOTE_MS = 1000;
/** Voz en Opus a 24 kbps ≈ 550 KB en 3 minutos. */
export const VOICE_NOTE_BITRATE = 24000;
/** Título provisorio de una conversación que arranca con una nota de voz. */
export const VOICE_NOTE_CONVERSATION_TITLE = 'Nota de voz';

// Preferencia: Chrome/Android (webm/opus), Safari/iOS (mp4), Firefox (ogg).
export const VOICE_NOTE_MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

export function pickVoiceNoteMimeType(isTypeSupported: (type: string) => boolean): string | null {
  for (const candidate of VOICE_NOTE_MIME_CANDIDATES) {
    try {
      if (isTypeSupported(candidate)) return candidate;
    } catch {
      /* navegador que tira en isTypeSupported: probar el siguiente */
    }
  }
  return null;
}

/** 'audio/webm;codecs=opus' → 'audio/webm' (lo que acepta el server). */
export function baseMimeType(mime: string): string {
  const base = (mime || '').split(';')[0].trim().toLowerCase();
  return base || 'audio/webm';
}

export function isValidVoiceNoteDuration(ms: number): boolean {
  return ms >= MIN_VOICE_NOTE_MS;
}

export function clampVoiceNoteDuration(ms: number): number {
  return Math.min(Math.max(Math.round(ms), 0), MAX_VOICE_NOTE_MS);
}

/** 72000 → '1:12' */
export function formatVoiceNoteDuration(ms: number): string {
  const totalSeconds = Math.floor(Math.max(ms, 0) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Nota de voz grabada que todavía no tiene texto (no puede entrar a un lote). */
export function isUntranscribedVoiceNote(message: { voiceNote?: unknown; content?: string }): boolean {
  return !!message.voiceNote && !message.content;
}

/**
 * Audios guardados cuyo mensaje ya no existe. Las notas ya transcriptas conservan
 * su audio (para poder escucharlas) hasta cerrar o descartar la conversación.
 */
export function findOrphanVoiceNoteIds(
  storedIds: string[],
  conversations: { messages: { id: string; voiceNote?: unknown; content?: string }[] }[]
): string[] {
  const live = new Set<string>();
  for (const conv of conversations) {
    for (const m of conv.messages) {
      if (m.voiceNote) live.add(m.id);
    }
  }
  return storedIds.filter((id) => !live.has(id));
}
